/* eslint-disable react-hooks/exhaustive-deps */
import { AdministrationApi, PocApi } from '@Apis';
import {
  AppForSa2AppType,
  DeProvisionStatus,
  DelegatorAccountResDto,
  DeprovisioningAppResDto,
  ReAuthAvailableAppResDto,
} from '@Types/administrator';
import { useEffect, useState } from 'react';
import { useOrgStore } from '@Models/useOrgModel';
import { AccountData } from '../ServiceAccount/types';
import { useTranslation } from 'react-i18next';

type PocStatus = Awaited<ReturnType<typeof PocApi.getAllOrgPocStatus>>['data'];
type OrgId = string;

export function useDelegatorAccount() {
  const [accounts, setAccounts] = useState<AccountData[]>([]);
  const [loading, setLoading] = useState(false);
  const { orgsMap } = useOrgStore();
  const { t } = useTranslation();

  const fetchData = async () => {
    let delegatorAccountData: DelegatorAccountResDto[] = [];
    let appReAuthStatus: ReAuthAvailableAppResDto = {};
    let appDeprovisioningStatus: DeprovisioningAppResDto = {};
    let pocStatus: PocStatus = {};
    try {
      [
        delegatorAccountData,
        appReAuthStatus,
        appDeprovisioningStatus,
        pocStatus,
      ] = await Promise.all([
        (await AdministrationApi.apiGetDelegatorAccount()).data,
        (await AdministrationApi.apiGetAllReAuthAvailableApp()).data,
        (await AdministrationApi.apiGetAllDeprovisioningApp()).data,
        (await PocApi.getAllOrgPocStatus()).data,
      ]);
    } catch (e) {
      console.error(e);
      throw new Error('apiGetDelegatorAccount error');
    }
    const _accounts: Record<OrgId, AccountData> = {};
    for (const item of delegatorAccountData) {
      if (!orgsMap.has(item.companyGuid)) continue;

      if (!_accounts[item.companyGuid]) {
        _accounts[item.companyGuid] = {
          orgId: item.companyGuid,
          orgName: orgsMap.get(item.companyGuid) as string,
          apps: [],
        };
      }

      _accounts[item.companyGuid].apps.push({
        email: item.email,
        app: item.app,
        endDate: item.endDate,
        valid: item.valid,
        status: item.status,
        extra: item.extra,
        provisionFromGraphapi: item.provisionFromGraphapi,
        migrationStatus: item.migrationStatus,
        isPoc: pocStatus[item.companyGuid]?.exchange === true,
        reAuthDisabled: true,
        provisionType: item.provisionType,
      });
    }

    for (const orgId in _accounts) {
      const orgApps = _accounts[orgId].apps.sort(
        (a, b) =>
          AppForSa2AppType[a.app as keyof typeof AppForSa2AppType] -
          AppForSa2AppType[b.app as keyof typeof AppForSa2AppType]
      );
      _accounts[orgId].apps = orgApps;

      if (appReAuthStatus[orgId]) {
        // has some app in provisioning, need disable reAuth
        const provisioningApps = appReAuthStatus[orgId];
        for (const appData of _accounts[orgId].apps) {
          if (provisioningApps.includes(appData.app)) {
            appData.reAuthDisabled = false;
          }
        }
      }

      if (appDeprovisioningStatus[orgId]) {
        // has some app in deprovisioning, status need show
        // "Removing the service account" | "Unable to remove the service account"
        const deprovisioningApps = appDeprovisioningStatus[orgId];
        for (const appData of _accounts[orgId].apps) {
          const deprovisioningStatus = deprovisioningApps[appData.app]?.status;
          if (deprovisioningStatus) {
            appData.deprovisioningStatus = deprovisioningStatus;
          }
          if (deprovisioningStatus === DeProvisionStatus.DeProvisioning) {
            _accounts[orgId].deprovisioning = true;
          }
        }
      }
    }

    orgsMap.forEach((value, key) => {
      if (key === 'all' || _accounts[key]) {
        return;
      }
      _accounts[key] = {
        orgId: key,
        orgName: value,
        apps: [],
      };
    });

    setAccounts(
      Object.values(_accounts).sort((a, b) =>
        a.orgName.toLowerCase() > b.orgName.toLowerCase() ? -1 : 1
      )
    );
  };

  const reFetchData = () => {
    setLoading(true);
    fetchData()
      .catch(() => {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    fetchData()
      .catch(() => {
        window.showNotification({
          titleMsg: t('common.apiException.internalError'),
          severity: 'error',
        });
      })
      .finally(() => {
        setLoading(false);
      });
    return () => {
      setAccounts([]);
      setLoading(false);
    };
  }, []);

  return { accounts, reFetchData, loading };
}
